import { createSelector } from 'reselect';

import {
  APPOINTMENT_EVENT_TYPE_ENUM,
  APPOINTMENT_STATUSES,
  APPOINTMENT_TASKS_STATUS,
  APPOINTMENT_TASK_TYPES,
} from 'constants/AppointmentConstants';
import { ADVICE_STATUS, CHECKLIST_BILLING } from 'constants/ChecklistConstants';
import { PAYMENT_TYPE, SETTLED_PAYMENT_STATUS } from 'constants/PaymentConstants';
import { getAppointmentTasksByIds } from 'modules/appointmentTask/selectors';
import { getByIds as getEventsByIds } from 'modules/events/selectors';
import { getById as getInterventionById } from 'modules/interventions/selectors/base';
import { getLockerById } from 'modules/lockers/selectors';
import { getCurrentUserIsReadOnly, } from 'modules/users/selectors/currentUser';
import { getDealerById } from 'selectors/dealerSelectors';
import { getUserById } from 'selectors/userSelectors';
import { getAdvices } from 'services/ChecklistUtils';

import { getById } from './general';

const getRoot = state => state;

const getState = state => state.appointments.ui.details;

export const getSelectedId = createSelector(getState, state => state.selectedId);

export const isAppointmentSending = createSelector(getState, state => state.isSending);

export const getSelectedAppointment = createSelector(getRoot, getSelectedId, getById);

export const getInterventionId = createSelector(getSelectedAppointment, appointment => appointment?.interventionId);

const getCreatorId = createSelector(getSelectedAppointment, appointment => appointment?.createdBy);

const getAppointmentTasksIds = createSelector(getSelectedAppointment, appointment => appointment?.appointmentTasksIds ?? []);

const getEventIds = createSelector(getSelectedAppointment, appointment => appointment?.events);

export const getDealerId = createSelector(getSelectedAppointment, appointment => appointment?.dealerId);

export const getDealerIdById = createSelector(getById, appointment => appointment?.dealerId);

export const getIntervention = createSelector(getRoot, getInterventionId, getInterventionById);

export const getDealer = createSelector(getRoot, getDealerId, getDealerById);

export const isAllowedOnlinePayment = createSelector(getDealer, ({ allowOnlinePayment }) => allowOnlinePayment);

export const getCreator = createSelector(getRoot, getCreatorId, getUserById);

export const getAppointmentTasks = createSelector(getRoot, getAppointmentTasksIds, getAppointmentTasksByIds);

export const getEvents = createSelector(getRoot, getEventIds, getEventsByIds);

export const canSave = createSelector(
  getSelectedAppointment,
  getCurrentUserIsReadOnly,
  (appointment, isReadonly) => !isReadonly && appointment?.status !== APPOINTMENT_STATUSES.CLOSED
);

export const isLoadingShareLink = createSelector(getState, state => state.isLoadingShareLink);

export const getShareLink = createSelector(getById, ({ shareLink } = {}) => shareLink);

export const isNotClosable = createSelector(
  getAppointmentTasks,
  tasks => tasks.some(task => task.status === APPOINTMENT_TASKS_STATUS.PREPARED) ?? false
);

export const isUpdatingStatus = createSelector(getState, state => state.isUpdatingStatus);

export const isDeleting = createSelector(getState, state => state.isDeleting);

export const getDriverDocumentsTasks = createSelector(getAppointmentTasks, tasks =>
  tasks.find(item => item.task.type === APPOINTMENT_TASK_TYPES.DRIVER_DOCUMENTS));

export const getBillingTask = createSelector(getAppointmentTasks, tasks =>
  tasks.find(item => item.task.type === APPOINTMENT_TASK_TYPES.BILLING));

const isBillingPayed = createSelector(getBillingTask, getIntervention, (task, intervention) => {
  const checklist = intervention?.checklists?.find(({ type }) => type === CHECKLIST_BILLING);
  const { type, status } = checklist?.payment ?? {};
  return type === PAYMENT_TYPE.FULL && SETTLED_PAYMENT_STATUS.includes(status);
});

export const hasPaidBillingAdvices = createSelector(
  getIntervention,
  /** @return {boolean}  */ intervention => {
    const checklist = intervention?.checklists?.find(({ type }) => type === CHECKLIST_BILLING);
    const allAdvices = getAdvices(checklist);
    return allAdvices.some(({ status }) => status === ADVICE_STATUS.PAID);
  }
);

export const isBillingToggleDisabled = createSelector(
  isBillingPayed,
  hasPaidBillingAdvices,
  (isPayed, haveBillingItems) => isPayed || haveBillingItems
);

export const isBookingDropOffModalVisible = createSelector(getState, state => state.isBookingDropOffModalVisible);

export const isBookingPickUpModalVisible = createSelector(getState, state => state.isBookingPickUpModalVisible);

export const getRepairOrderTask = createSelector(getAppointmentTasks, tasks =>
  tasks.find(item => item.task.type === APPOINTMENT_TASK_TYPES.REPAIR_ORDER));

export const getKeyDepositTask = createSelector(getAppointmentTasks, tasks =>
  tasks.find(
    ({ status, task }) =>
      task.type === APPOINTMENT_TASK_TYPES.KEY_DEPOSIT && status !== APPOINTMENT_TASKS_STATUS.CANCELED
  ));

const getKeyDepositLocker = createSelector(
  getRoot,
  getKeyDepositTask,
  (state, task) => task && getLockerById(state, task.vaultId)
);

export const getKeyDepositTaskComplete = createSelector(
  getKeyDepositTask,
  getKeyDepositLocker,
  (task, vault) => task && { ...task, vault }
);

export const getRentalVehicleTask = createSelector(getAppointmentTasks, tasks =>
  tasks.find(({ task }) => task.type === APPOINTMENT_TASK_TYPES.RENTAL_VEHICLE));

export const isReadyApplication = createSelector(getState, state => state.isReady);

export const getKeyRetrieveTask = createSelector(getAppointmentTasks, tasks =>
  tasks.find(
    ({ status, task }) =>
      task.type === APPOINTMENT_TASK_TYPES.KEY_RETRIEVE && status !== APPOINTMENT_TASKS_STATUS.CANCELED
  ));

const getKeyRetrieveLocker = createSelector(
  getRoot,
  getKeyRetrieveTask,
  (state, task) => task && getLockerById(state, task.vaultId)
);

export const getKeyRetrieveTaskComplete = createSelector(
  getKeyRetrieveTask,
  getKeyRetrieveLocker,
  (task, vault) => task && { ...task, vault }
);

export const getClientDocumentsTask = createSelector(getAppointmentTasks, tasks =>
  tasks.find(({ task }) => task.type === APPOINTMENT_TASK_TYPES.DRIVER_DOCUMENTS));

const getEventIdsByAppointmentId = createSelector(getById, appointment => appointment?.events);

const getEventsByAppointmentId = createSelector(getRoot, getEventIdsByAppointmentId, getEventsByIds);

export const isAlreadyMarkAsSent = createSelector(getEventsByAppointmentId, (events = []) =>
  events.find(({ eventType }) =>
    [
      APPOINTMENT_EVENT_TYPE_ENUM.SENT,
      APPOINTMENT_EVENT_TYPE_ENUM.MANUALLY_SENT,
      APPOINTMENT_EVENT_TYPE_ENUM.PICK_UP_LOCKER_INFO_SENT,
      APPOINTMENT_EVENT_TYPE_ENUM.DROP_OFF_LOCKER_INFO_SENT,
    ].includes(eventType)));
